import { AllLinksSection } from 'components/city-blocks/all-links-section';
import { HeaderCopy } from 'components/city-blocks/header-copy';
import { LinksBlock } from 'components/city-blocks/links-block';
import { MaxWidthContainer } from 'components/city-blocks/max-width-container';
import { MetroImage } from 'components/city-blocks/metro-image';
import { HeroBlock } from 'components/hero-block/hero-block';
import Layout from 'components/layout';
import { map, mapValues } from 'lodash';
import Head from 'next/head';
import Link from 'next/link';
import React from 'react';
import { NoFollowLink } from 'shared/no-follow-link';
import { zipsRootBreadcrumbs } from 'utils/breadcrumb-util';
import { LINKS_BLOCK_LIMIT } from 'utils/constants';
import { getFrontEndUrl } from 'utils/environment-util';
import { RegionInfoFragment } from 'utils/gqlReactTypings.generated.d';
import { memoizedCityPageCities, memoizedRegionsById, memoizedZipPagesByRegionId } from 'utils/memoized-data-structure';
import { reduceZipPage, ZipPage } from 'utils/types';
import { encodeRegionPageHref, encodeZipPageHref } from 'utils/urls';

interface IProps {
  zipPages: ZipPage[];
  zipsByRegion: { [identifier: string]: ZipPage[] };
  regionsById: { [identifier: string]: RegionInfoFragment };
}
const ZIP_HOME_META_DESC =
  'Shef delivers authentic cuisines in cities across the States. Check to see if Shef delivers delicious homemade food near you! Search our food delivery cities!';
const HEADER_COPY_TITLE = 'Find a Local Delivery or Pickup Option Near You For Delicious Homemade Food!';

const invokeHeaderCopyContent: ({ numZips: number }) => React.ReactElement = ({ numZips }): React.ReactElement => (
  <span>
    Shef delivers traditional homemade food to over {numZips} ZIP Codes throughout the US and has food pickup in many
    more! With thousands of local Shefs & dishes to choose from, you're sure to find the perfect homemade dish for
    breakfast, lunch, or dinner! Browse by metro, city, or{' '}
    <NoFollowLink
      href={getFrontEndUrl()}
      className='text-neutral-500 underline underline-offset-2 hover:text-neutral-700'>
      search by ZIP
    </NoFollowLink>
  </span>
);

const ZipCodesWeServe: React.FC<IProps> = ({ zipPages, zipsByRegion, regionsById }) => {
  const title = 'See All US ZIP Codes Where Our Shefs Cook Homemade Food!';
  const heroText = 'ZIP Codes Where Shef Delivers';

  return (
    <Layout>
      <Head>
        <title>{title}</title>
        <meta name='og:title' content={title} />
        <meta name='description' content={ZIP_HOME_META_DESC} />
      </Head>
      <HeroBlock className='mb-10' heroText={heroText} crumbs={zipsRootBreadcrumbs} />
      <MaxWidthContainer className='px-4 md:px-0'>
        <HeaderCopy
          className='mb-12'
          title={HEADER_COPY_TITLE}
          content={invokeHeaderCopyContent({ numZips: zipPages.length })}
        />

        <div className='mb-10 flex flex-col flex-wrap gap-10 md:flex-row md:gap-4'>
          {map(
            zipsByRegion,
            (regionZipPages, regionId) =>
              regionsById[regionId] && (
                <div className='mb-10 flex flex-col items-center md:items-start'>
                  <MetroImage region={regionsById[regionId]} className='mb-3' />
                  <LinksBlock
                    key={regionId}
                    title={
                      <span>
                        Popular ZIP codes in
                        <br />
                        {regionsById[regionId].displayName}
                      </span>
                    }
                    titleHref={encodeRegionPageHref(regionsById[regionId])}>
                    {regionZipPages.map((zipPage) => (
                      <Link key={zipPage.id} href={encodeZipPageHref(zipPage, zipPage.zipCode)}>
                        {zipPage.zipCode}
                      </Link>
                    ))}
                  </LinksBlock>
                </div>
              )
          )}
        </div>
      </MaxWidthContainer>

      <AllLinksSection title='All ZIP codes Shef serves' columnsClassName='columns-2 gap-8 md:columns-5'>
        {zipPages.map((zipPage) => (
          <div className='mb-2 w-full gap-2' key={zipPage.id}>
            <Link
              className='cursor-pointer text-lg font-normal leading-normal text-neutral-500 underline underline-offset-2 hover:text-neutral-700'
              href={encodeZipPageHref(zipPage, zipPage.zipCode)}>
              {zipPage.zipCode}
            </Link>
          </div>
        ))}
      </AllLinksSection>
    </Layout>
  );
};

export async function getStaticProps() {
  const cityPageCities = await memoizedCityPageCities();
  const zipPagesByRegionId = await memoizedZipPagesByRegionId();
  const regionsById = await memoizedRegionsById();

  return {
    props: {
      zipPages: cityPageCities
        .map((cityPage) => reduceZipPage(cityPage, cityPage.zipCode))
        .sort((a, b) => a.zipCode.localeCompare(b.zipCode)),
      zipsByRegion: mapValues(zipPagesByRegionId, (array) => array.slice(0, LINKS_BLOCK_LIMIT)),
      regionsById,
    },
  };
}

export default ZipCodesWeServe;
